/**
 * Studio FTUX constants shared across the stack. These live in `src/constants`
 * (rather than a frontend feature module) because the Express session
 * middleware imports them server-side; keeping them free of browser-only
 * dependencies means importing here can never break server startup.
 *
 * A CRM "Open portal" preview magic link carries a preview param as its post-login
 * destination, and the session middleware reads it to skip the single-use
 * confirmation interstitial for these freshly minted links. Studio workspaces send
 * the studio-prefixed one, classic workspaces the plain one — the two are otherwise
 * interchangeable, so always test a destination with `isClientPreviewNextPath`
 * rather than one param.
 */
export const STUDIO_CLIENT_PREVIEW_PARAM = 'studioClientPreview';
export const CLIENT_PREVIEW_PARAM = 'clientPreview';
export const CLIENT_PREVIEW_VALUE = 'true';

/**
 * True when a magic-link destination asks for a client preview, on either
 * workspace kind. Takes the raw `next` value (a path plus query), so it has to
 * find the query itself.
 */
export const isClientPreviewNextPath = (next: string): boolean => {
  const queryStart = next.search(/[?&]/);
  if (queryStart === -1) {
    return false;
  }
  const params = new URLSearchParams(next.slice(queryStart + 1));
  return (
    params.get(CLIENT_PREVIEW_PARAM) === CLIENT_PREVIEW_VALUE ||
    params.get(STUDIO_CLIENT_PREVIEW_PARAM) === CLIENT_PREVIEW_VALUE
  );
};

/**
 * Same as `isClientPreviewNextPath`, but only for same-origin absolute paths.
 * Magic-link `next` values are stored verbatim, so any auto-login bypass must
 * reject URLs that could navigate away from the client portal after sign-in.
 */
export const isSafeClientPreviewNextPath = (next: string): boolean => {
  if (!next.startsWith('/') || next.startsWith('//') || next.includes('\\')) {
    return false;
  }
  return isClientPreviewNextPath(next);
};

// The preview link carries the client-side "Getting started" progress bar
// percentage, because the client session can't read the studio owner's
// onboarding-task-status record itself. It reflects whether the owner has
// published their first app: full bar when done, half otherwise.
export const STUDIO_CLIENT_PREVIEW_TASK_PROGRESS_PARAM = 'taskProgress';
export const STUDIO_CLIENT_PREVIEW_PROGRESS_COMPLETE = 100;
export const STUDIO_CLIENT_PREVIEW_PROGRESS_IN_PROGRESS = 50;

/**
 * PostHog workspace group property that puts a portal in the Studio cohort — the
 * only Studio cohort, and what the `studio-pricing` flag targets. Must match
 * backend `segmentcli.StudioWorkspaceGroupTrait` / Value (`"studio"` / `"true"`).
 * SSR flag evaluation passes this via `groupProperties` so Studio flags resolve on
 * first paint before `$groupidentify` is ingested.
 */
export const STUDIO_WORKSPACE_GROUP_PROPERTY = 'studio';
export const STUDIO_WORKSPACE_GROUP_PROPERTY_VALUE = 'true';

/**
 * The Studio onboarding entry a signup came through, and the value persisted as
 * `PortalConfig.referrer` (mirrors backend `portalconfig.StudioReferrer`). Both
 * entries are one flow unlocking the identical product; they differ only in the
 * value recorded here, which is attribution and must never gate behavior.
 *
 * These live here rather than with the onboarding route constants because the
 * Express server reads the persisted value during SSR flag evaluation, and this
 * module is guaranteed free of browser-only dependencies.
 */
export const STUDIO_REFERRER = 'studio';
export const STUDIO_PRICING_REFERRER = 'studio-pricing';

/**
 * The CRM page stamps this `window.name` on the Internal Portal tab before it
 * opens a "test as a client" preview tab. From the preview, the client-portal
 * "Return to CRM" button re-targets this name with `window.open('', name)` to
 * switch the browser back to that existing Internal Portal tab — `opener.focus()`
 * is blocked by browsers as focus-stealing, but targeting an existing named tab
 * on a user click is honored.
 */
export const STUDIO_INTERNAL_PORTAL_TAB_NAME = 'assemblyInternalPortal';
